// A sheet's own filter — the bar above the grid.
//
// Every GI.* formula on the sheet and every database on it applies it: pick
// layer 2 and the whole sheet is about layer 2. It lives on the sheet rather
// than in a cell because it is not something the sheet COMPUTES, it is what the
// sheet is ABOUT — and a filter hidden in a cell is one nobody sees applied.
// On Areas and Features specifically, it does one thing more: it hides rows
// too (`sheetFilterHiddenRows`), since those sheets' own rows ARE the
// project's data, not just formulas reading it.
//
// A clause carries zero or more literal `values` (ORed together — a manual
// multi-select) OR an `op: 'visible'` tracking whichever layers are visible
// right now; the two never combine on one clause. Either way it reaches
// GI.* formulas and databases as a single `field=a|b|c` filter-text clause.
//
// A clause whose field the source does not carry is skipped rather than
// matching nothing: a usages table on a sheet filtered by layer is still the
// usages table.

import type { Sheet } from './types';

/** The one field "Visible only" means anything for — what the layer panel's eye icon governs. */
export function isVisibilityField(field: string): boolean {
  return field.toLowerCase() === 'layerid';
}

/**
 * The live "visible" values for a field, from whatever reports the layer
 * panel — `undefined` for any other field, or when nothing can say (no
 * Giraffe, a demo). The one definition GI.* formulas, databases and the
 * Areas/Features row hiding all share, so they cannot disagree about it.
 */
export function layerVisibility(
  visibleLayers: (() => readonly string[]) | undefined
): (field: string) => readonly string[] | undefined {
  return (field) => (isVisibilityField(field) && visibleLayers ? visibleLayers() : undefined);
}

/**
 * A value no layer id carries. A "Visible only" clause with every layer
 * hidden has to match NOTHING — dropping it would let the formulas count the
 * whole project while the grid shows no rows.
 */
const NO_VALUE = '\u0000';

export interface SheetFilterClause {
  field: string;
  /** Zero or more literal values, ORed together — empty means any (the clause is there to be picked, not yet applied). Ignored when `op` is set. */
  values: string[];
  /**
   * "Visible only" — whichever values are visible right now (the layer
   * panel's eye icon), a moving target rather than a literal someone picked.
   * Only meaningful for the layer field; absent (the default) is a plain
   * `values` match.
   */
  op?: 'visible';
}

/**
 * Most system sheets show everything the project has, by definition — a
 * filtered Usages or Blocks sheet would silently stop being the usages table.
 * Areas and Features are the exception: both already carry custom calc
 * columns a user adds themselves (GI.* formulas, databases), AND their own
 * built-in rows are the thing being filtered when the user picks Layer/Usage
 * there — see `hiddenRowsFor` below for that second effect.
 */
export function canFilterSheet(sheet: Sheet): boolean {
  return sheet.role === undefined || sheet.role === 'areas' || sheet.role === 'features';
}

/**
 * The sheet filter as filter text, for a source carrying `fields`. "" for
 * none. Multiple `values` join with `|` — `data/filter.ts`'s own "any of
 * these" syntax — so a multi-select clause reaches GI.* formulas and
 * databases exactly like a single value always did. A "visible only" clause
 * resolves through `visibleValuesFor` (the live layer-panel set) and is
 * skipped — not "any", genuinely dropped — when nothing can answer that for
 * this field, same as a clause naming a field the source does not carry.
 * With every layer hidden it matches nothing, as the grid does.
 */
export function sheetFilterText(
  sheet: Sheet | undefined,
  fields: readonly string[],
  visibleValuesFor: (field: string) => readonly string[] | undefined = () => undefined
): string {
  if (!sheet?.sheetFilter) return '';
  const known = new Set(fields);
  const parts: string[] = [];
  for (const c of sheet.sheetFilter) {
    if (c.field === '' || !known.has(c.field)) continue;
    let values: readonly string[];
    if (c.op === 'visible') {
      const live = visibleValuesFor(c.field);
      if (!live) continue;
      values = live.length > 0 ? live : [NO_VALUE];
    } else {
      values = c.values;
    }
    if (values.length === 0) continue;
    const joined = values.join('|');
    const quoted = /[,'"]/.test(joined) && !joined.includes('"') ? `"${joined}"` : joined;
    parts.push(`${c.field}=${quoted}`);
  }
  return parts.join(', ');
}

/** Is anything actually picked? An open bar with every value on "any" filters nothing. */
export function sheetFilterActive(sheet: Sheet | undefined): boolean {
  return (sheet?.sheetFilter ?? []).some(clauseIsSet);
}

/** A clause that filters something: a picked field with values, or "Visible only" on the layer field. */
function clauseIsSet(c: SheetFilterClause): boolean {
  if (c.field === '') return false;
  return c.op === 'visible' ? isVisibilityField(c.field) : c.values.length > 0;
}

/**
 * Areas/Features ARE the project's own rows, unlike an ordinary sheet whose
 * rows are just whatever the user put there — so on these two, the sheet
 * filter hides rows too, not only scoping GI.* formulas. A clause's field is
 * matched against the sheet's OWN header row (case-insensitive) rather than
 * assumed to be a fixed column, since Areas and Features don't share a
 * layout; a clause naming a column this sheet doesn't have is skipped, same
 * as `sheetFilterText` already does for a formula/database.
 */
export function sheetFilterHiddenRows(
  sheet: Sheet,
  colForHeader: (header: string) => number | undefined,
  cellTextAt: (row: number, col: number) => string,
  firstDataRow: number,
  lastRow: number,
  /** The live "visible" set for a clause's field — e.g. layerId's from the layer panel. `undefined` when the field has no such concept. */
  visibleValuesFor: (field: string) => Iterable<string> | undefined = () => undefined
): Set<number> {
  const out = new Set<number>();
  if (sheet.role !== 'areas' && sheet.role !== 'features') return out;
  type Clause = { col: number; test: (text: string) => boolean };
  const clauses = (sheet.sheetFilter ?? [])
    .filter(clauseIsSet)
    .map((c): Clause | null => {
      const col = colForHeader(c.field);
      if (col === undefined) return null;
      let allowed: Iterable<string>;
      if (c.op === 'visible') {
        const live = visibleValuesFor(c.field);
        // No visibility signal for this field at all — skip the clause
        // rather than hiding every row on a predicate that cannot be tested.
        if (!live) return null;
        allowed = live;
      } else {
        allowed = c.values;
      }
      const lowered = new Set([...allowed].map((v) => v.trim().toLowerCase()));
      return { col, test: (text) => lowered.has(text.trim().toLowerCase()) };
    })
    .filter((c): c is Clause => c !== null);
  if (clauses.length === 0) return out;
  for (let r = firstDataRow; r <= lastRow; r++) {
    for (const c of clauses) {
      if (!c.test(cellTextAt(r, c.col))) {
        out.add(r);
        break;
      }
    }
  }
  return out;
}
